import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  ChatsCircle, Envelope, EnvelopeOpen, CircleNotch, 
  ArrowLeft, PaperPlaneTilt, User, Clock
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function MessagesPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/marketplace/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user) {
      navigate("/marketplace");
      return;
    }
    fetchConversations();
  }, [user, fetchConversations, navigate]);

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setLoadingMessages(true);
    
    try {
      const res = await axios.get(`${API}/api/marketplace/messages/${conv.listing_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages || []);
      
      // Update unread count in local state
      setConversations(prev => prev.map(c => 
        c.listing_id === conv.listing_id ? { ...c, unread_count: 0 } : c
      ));
    } catch (err) {
      toast.error("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/marketplace/messages`, {
        listing_id: selectedConversation.listing_id,
        content: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => [...prev, res.data]);
      setNewMessage("");
      
      // Update conversation preview
      setConversations(prev => prev.map(c => 
        c.listing_id === selectedConversation.listing_id 
          ? { ...c, messages: [...c.messages, res.data] }
          : c
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-IE', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/marketplace")}
          className="flex items-center gap-2 text-[#6B6661] hover:text-[#0F766E] mb-6"
        >
          <ArrowLeft size={20} />
          Back to Marketplace
        </button>

        <h1 className="font-serif text-2xl sm:text-3xl text-[#33302E] mb-6">
          Messages
        </h1>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <CircleNotch size={32} className="animate-spin text-[#0F766E]" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="bg-white border border-[#E0D4C8] p-12 text-center">
            <ChatsCircle size={48} className="mx-auto text-[#D1D5DB] mb-4" />
            <h2 className="text-lg text-[#33302E] mb-2">No messages yet</h2>
            <p className="text-[#6B6661] mb-4">
              When you contact sellers about listings, your conversations will appear here.
            </p>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64] transition-colors"
            >
              Browse Marketplace
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-[#E0D4C8] grid md:grid-cols-3 min-h-[600px]">
            {/* Conversations List */}
            <div className="border-r border-[#E0D4C8] overflow-y-auto">
              {conversations.map((conv) => (
                <button
                  key={conv.listing_id}
                  onClick={() => selectConversation(conv)}
                  className={`w-full p-4 text-left border-b border-[#E0D4C8] hover:bg-[#F9FAFB] transition-colors ${
                    selectedConversation?.listing_id === conv.listing_id ? "bg-[#F0FDF4]" : ""
                  }`}
                  data-testid={`conversation-${conv.listing_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-[#33302E] truncate">
                        {conv.listing_title}
                      </h3>
                      <p className="text-sm text-[#6B6661] truncate">
                        {conv.other_user_email}
                      </p>
                      {conv.messages && conv.messages.length > 0 && (
                        <p className="text-sm text-[#8B8580] truncate mt-1">
                          {conv.messages[conv.messages.length - 1].content}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {conv.messages && conv.messages.length > 0 && (
                        <span className="text-xs text-[#8B8580]">
                          {formatTime(conv.messages[conv.messages.length - 1].created_at)}
                        </span>
                      )}
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 bg-[#0F766E] text-white text-xs rounded-full flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Messages Panel */}
            <div className="md:col-span-2 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-[#E0D4C8]">
                    <Link 
                      to={`/marketplace/${selectedConversation.listing_id}`}
                      className="font-medium text-[#33302E] hover:text-[#0F766E]"
                    >
                      {selectedConversation.listing_title}
                    </Link>
                    <p className="text-sm text-[#6B6661]">
                      {selectedConversation.other_user_email}
                    </p>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center py-8">
                        <CircleNotch size={24} className="animate-spin text-[#0F766E]" />
                      </div>
                    ) : messages.length === 0 ? (
                      <p className="text-center text-[#6B6661] py-8">
                        No messages in this conversation yet.
                      </p>
                    ) : (
                      messages.map((msg) => {
                        const isMine = msg.sender_id === user.id;
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                          >
                            <div className={`max-w-[70%] ${isMine ? "order-2" : ""}`}>
                              <div className={`p-3 ${
                                isMine 
                                  ? "bg-[#0F766E] text-white" 
                                  : "bg-[#F6E9DD] text-[#33302E]"
                              }`}>
                                <p className="whitespace-pre-wrap">{msg.content}</p>
                              </div>
                              <p className={`text-xs text-[#8B8580] mt-1 ${isMine ? "text-right" : ""}`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Message Input */}
                  <form onSubmit={sendMessage} className="p-4 border-t border-[#E0D4C8]">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2 border border-[#E0D4C8] text-[#33302E] focus:outline-none focus:border-[#0F766E]"
                        data-testid="message-input"
                      />
                      <button
                        type="submit"
                        disabled={sending || !newMessage.trim()}
                        className="px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64] disabled:opacity-50 transition-colors"
                      >
                        {sending ? (
                          <CircleNotch size={20} className="animate-spin" />
                        ) : (
                          <PaperPlaneTilt size={20} />
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-[#8B8580]">
                  <div className="text-center">
                    <Envelope size={48} className="mx-auto mb-4" />
                    <p>Select a conversation to view messages</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
